<template>
  <div class="flex h-full flex-col">
    <h3 class="body-1-bold mb-12 mt-24">
      {{ i18n(translations.title) }}
    </h3>

    <RevList>
      <RevListItemStatic>
        <template #label>
          {{ i18n(translations.activity) }}
        </template>

        <template #description>
          {{ activity }}
        </template>
      </RevListItemStatic>

      <RevListItemStatic>
        <template #label>
          {{ i18n(translations.markets) }}
        </template>

        <template #description>
          {{ markets }}
        </template>
      </RevListItemStatic>

      <RevListItemStatic>
        <template #label>
          {{ i18n(translations.categories) }}
        </template>

        <template #description>
          {{ categories }}
        </template>
      </RevListItemStatic>
    </RevList>

    <p class="body-1-bold mb-12 mt-24">
      {{ i18n(translations.address) }}
    </p>

    <AddressCard :address="metadata.address!" class="mb-24" />

    <RevInfoBlock
      v-if="apiError"
      class="mb-24"
      :content="apiError"
      :icon="IconWarning"
      :title="i18n(translations.errorTitle)"
      variant="danger"
    />

    <RevButton full-width="always" variant="primary" @click="submitRule">
      {{ ctaText }}
    </RevButton>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'

import { merchantsAPI } from '@backmarket/http-api'
import { $httpFetch } from '@backmarket/nuxt-module-http/$httpFetch'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useTheToast } from '@backmarket/nuxt-module-toast/useTheToast'
import { isEmpty } from '@backmarket/utils/object/isEmpty'
import { RevButton } from '@ds/components/Button'
import { RevInfoBlock } from '@ds/components/InfoBlock'
import { RevList } from '@ds/components/List'
import { RevListItemStatic } from '@ds/components/ListItemStatic'
import { IconWarning } from '@ds/icons/IconWarning'

import AddressCard from '../../../../../AddressesManager/components/AddressCard/AddressCard.vue'
import type { AddressRulePayload } from '../../../../AddressRules.constants'

import translations from './Confirmation.translations'

const { openSuccessToast } = useTheToast()
const ACTIVITIES = {
  productReturn: 'PRODUCT_RETURN',
  buyback: 'BUYBACK',
  backship: 'BACKSHIP',
  productRefund: 'PRODUCT_REFUND',
  productRepair: 'PRODUCT_REPAIR_REPLACE',
}

const { payload, metadata, ruleToEdit } = defineProps<{
  payload: AddressRulePayload
  metadata: merchantsAPI.AddressRuleMetada
  ruleToEdit: merchantsAPI.AddressRule | Record<string, never>
}>()
const emit = defineEmits(['rule-submitted'])
const apiError = ref('')
const i18n = useI18n()

const activity = computed(() => {
  switch (payload.service) {
    case ACTIVITIES.productReturn:
      return i18n(translations.productReturn)
    case ACTIVITIES.productRefund:
      return i18n(translations.productRefund)
    case ACTIVITIES.productRepair:
      return i18n(translations.productRepair)
    case ACTIVITIES.buyback:
      return i18n(translations.buyback)
    case ACTIVITIES.backship:
      return i18n(translations.backship)
    default:
      return ''
  }
})

const isEditing = computed(() => {
  return !isEmpty(ruleToEdit)
})

const ctaText = computed(() => {
  return isEditing.value ? i18n(translations.edit) : i18n(translations.create)
})

const categories = computed(() => {
  if (isEmpty(payload.categories)) {
    return i18n(translations.allCategories)
  }

  return i18n.list(
    (metadata.categories || [])
      .filter(({ id }) => payload.categories?.includes(id))
      .map(({ name }) => name),
  )
})
const markets = computed(() => {
  if (isEmpty(payload.markets)) {
    return i18n(translations.allMarkets)
  }

  return i18n.list(
    (payload.markets || []).map((market) => i18n.country(market)),
  )
})

async function submitRule() {
  const options: Record<string, unknown> = {
    body: {
      ...payload,
      confirm: true,
    },
  }

  if (isEditing.value) {
    options.pathParams = {
      id: ruleToEdit.id,
    }
  }

  try {
    await $httpFetch(
      isEditing.value
        ? merchantsAPI.putAddressRule
        : merchantsAPI.postAddressRule,
      options,
    )
    openSuccessToast()
    emit('rule-submitted')
  } catch (error) {
    apiError.value =
      error instanceof Error ? error.message : i18n(translations.errorTitle)
  }
}
</script>
